/* Browser-side brokerage geocoding. Runs in the browser because the Maps
 * key is HTTP-referrer restricted (server-side Places calls are refused).
 * Places "find from query" first; Geocoder as a fallback for plain street
 * addresses Places doesn't know as a business. */
import { hasCoords, withTimeout } from "./routeOptimizer";
import { gmapsAuthFailed, rejectOnGmapsAuthFailure } from "./gmapsAuth";

export const DEFAULT_LOCATION = "Saskatoon, SK";
export const GEOCODE_TIMEOUT_MS = 12000;
/* Errors that say nothing about the address itself — don't persist a
 * "failed" geocode status for these, and stop a bulk sync early. */
export const INFRA_ERRORS = ["MAPS_NOT_LOADED", "MAPS_AUTH_FAILED", "TIMEOUT", "PLACES_NOT_LOADED", "GEOCODER_NOT_LOADED"];
const BIAS_CENTER = { lat: 52.1332, lng: -106.67 };

/* Mirrors backend `brokerage_geocoder.geocode_query`: stored address wins
 * (city appended when the operator typed a bare street), else name + city. */
export function geocodeQueryFor(b, locationName) {
  const loc = (locationName || "").trim() || DEFAULT_LOCATION;
  const addr = (b?.office_address || "").trim();
  if (addr) return addr.includes(",") ? addr : `${addr}, ${loc}`;
  return `${b?.name || ""}, ${loc}`;
}

function toPoint(loc) {
  if (!loc) return null;
  const lat = typeof loc.lat === "function" ? loc.lat() : loc.lat;
  const lng = typeof loc.lng === "function" ? loc.lng() : loc.lng;
  return hasCoords({ lat, lng }) ? { lat, lng } : null;
}

function placesFind(g, query, host) {
  return new Promise((resolve) => {
    if (!g.places) {
      resolve({ error: "PLACES_NOT_LOADED" });
      return;
    }
    const svc = new g.places.PlacesService(host || document.createElement("div"));
    svc.findPlaceFromQuery(
      {
        query,
        fields: ["place_id", "name", "formatted_address", "geometry"],
        locationBias: { center: new g.LatLng(BIAS_CENTER.lat, BIAS_CENTER.lng), radius: 50000 },
      },
      (results, status) => {
        const r = Array.isArray(results) && results[0];
        const pt = r && toPoint(r.geometry && r.geometry.location);
        if (status !== "OK" || !pt) {
          resolve({ error: status || "ZERO_RESULTS" });
          return;
        }
        resolve({ ...pt, place_id: r.place_id || "", formatted_address: r.formatted_address || "" });
      },
    );
  });
}

function geocoderFind(g, query) {
  return new Promise((resolve) => {
    if (!g.Geocoder) {
      resolve({ error: "GEOCODER_NOT_LOADED" });
      return;
    }
    new g.Geocoder().geocode({ address: query, region: "ca" }, (results, status) => {
      const r = Array.isArray(results) && results[0];
      const pt = r && toPoint(r.geometry && r.geometry.location);
      if (status !== "OK" || !pt) {
        resolve({ error: status || "ZERO_RESULTS" });
        return;
      }
      resolve({ ...pt, place_id: r.place_id || "", formatted_address: r.formatted_address || "" });
    });
  });
}

/** Wrap a Google callback-style lookup so it can't hang: it rejects on
 * the 12s clock and the moment Google reports the key was rejected. */
function guarded(promise) {
  return withTimeout(
    Promise.race([promise, rejectOnGmapsAuthFailure(() => Object.assign(new Error("MAPS_AUTH_FAILED"), { code: "MAPS_AUTH_FAILED" }))]),
    GEOCODE_TIMEOUT_MS,
  ).catch((e) => ({ error: e && e.code === "TIMEOUT" ? "TIMEOUT" : (e && e.code) || String((e && e.message) || e) }));
}

/** @returns {Promise<{lat,lng,place_id,formatted_address,via}|{error:string}>} */
export async function findPlace(query, host) {
  const g = typeof window !== "undefined" && window.google && window.google.maps;
  if (!g) return { error: "MAPS_NOT_LOADED" };
  if (gmapsAuthFailed()) return { error: "MAPS_AUTH_FAILED" };
  const viaPlaces = await guarded(placesFind(g, query, host));
  if (!viaPlaces.error) return { ...viaPlaces, via: "places" };
  if (INFRA_ERRORS.includes(viaPlaces.error)) return viaPlaces;
  const viaGeocoder = await guarded(geocoderFind(g, query));
  if (!viaGeocoder.error) return { ...viaGeocoder, via: "geocoder" };
  if (INFRA_ERRORS.includes(viaGeocoder.error)) return viaGeocoder;
  return { error: `${viaPlaces.error} / ${viaGeocoder.error}` };
}
